import { LayoutUtils } from "@nms-ng2/app/shared/services/layout-manager/layout-utils";
import { TopologySettingsMap } from "../layout/circuit-layout.models";
import {
    CircuitElementType,
    DeviceType,
    CircuitElementState,
    TransponderMultirate,
    AmplifierType,
    TransmissionPositionMap,
    CircuitButton,
    CircuitButtonMap,
} from "./diagram.models";
import { Padding, SimpleLayoutElement } from "@nms-ng2/app/shared/services/layout-manager/layout-manager.models";
import { Transmission } from "@nms-ng2/app/modules/topology/links/links.model";
import { CIRCUIT_BLUE, CIRCUIT_NOT_ACTIVE, WHITE_COLOR } from "@nms-ng2/app/shared/constants/colors";
import { CellStateStyle } from "@maxgraph/core";

/**
 * Constante que contem as informações básicas de cada elemento topológico do circuito.
 */
export const TOPOLOGY_SETTINGS: TopologySettingsMap = {
    [CircuitElementType.DEVICE]: {
        [DeviceType.END]: {
            imagePath: "images/device-box.svg",
            width:     234,
            height:    212
        },
        [DeviceType.INTERMEDIATE_FULL]: {
            imagePath: "images/intermediate-full-box.svg",
            width:     160,
            height:    212
        },
        [DeviceType.INTERMEDIATE_HALF]: {
            imagePath: "images/intermediate-half-box.svg",
            width:     160,
            height:    106
        }
    },
    [CircuitElementType.MUX]: {
        [CircuitElementState.INITIAL]: {
            imagePath: "images/mux-gray.svg",
            width: 22,
            height: 173
        },
        [CircuitElementState.CONFIGURED]: {
            imagePath: "images/mux.svg",
            width: 22,
            height: 173
        },
        [CircuitElementState.NOT_CONFIGURED]: {
            imagePath: "images/mux-faded.svg",
            width: 22,
            height: 173
        },
    },
    [CircuitElementType.TRANSPONDER]: {
        [CircuitElementState.PRE_CONFIGURED]: {
            imagePath: "images/transponder.svg",
            width: 41,
            height: 36
        },
        [CircuitElementState.INITIAL]: {
            imagePath: "images/transponder-gray.svg",
            width: 41,
            height: 36
        },
        [CircuitElementState.CONFIGURED]: {
            [TransponderMultirate.MULTIRATE_1X_100]: {
                imagePath: "images/transponder-1.svg",
                width:     41,
                height:    36
            },
            [TransponderMultirate.MULTIRATE_2X_100]: {
                imagePath: "images/transponder-2.svg",
                width:     41,
                height:    36
            },
            [TransponderMultirate.MULTIRATE_3X_100]: {
                imagePath: "images/transponder-3.svg",
                width:     41,
                height:    36
            },
            [TransponderMultirate.MULTIRATE_4X_100]: {
                imagePath: "images/transponder-4.svg",
                width:     41,
                height:    36
            }
        },
        [CircuitElementState.NOT_CONFIGURED]: {
            imagePath: "images/transponder-faded.svg",
            width: 41,
            height: 36
        },
    },
    [CircuitElementType.OSC]: {
        [CircuitElementState.INITIAL]: {
            imagePath: "images/osc-gray.svg",
            width: 28,
            height: 28
        },
        [CircuitElementState.NOT_CONFIGURED]: {
            imagePath: "images/osc-faded.svg",
            width: 28,
            height: 28
        },
        [CircuitElementState.CONFIGURED]: {
            imagePath: "images/osc-clear.svg",
            width: 28,
            height: 28
        }
    },
    [CircuitElementType.AMPLIFIER]: {
        [AmplifierType.BOOSTER]: {
            [CircuitElementState.INITIAL]: {
                imagePath: "images/amp_booster-faded.svg",
                width:     22,
                height:    32
            },
            [CircuitElementState.NOT_CONFIGURED]: {
                imagePath: "images/amp_booster.svg",
                width:     22,
                height:    32
            },
            [CircuitElementState.CONFIGURED]: {
                imagePath: "images/amp_booster.svg",
                width:     22,
                height:    32
            }
        },
        [AmplifierType.PRE_AMP]: {
            [CircuitElementState.INITIAL]: {
                imagePath: "images/amp_preamp-faded.svg",
                width:     22,
                height:    32
            },
            [CircuitElementState.NOT_CONFIGURED]: {
                imagePath: "images/amp_preamp.svg",
                width:     22,
                height:    32
            },
            [CircuitElementState.CONFIGURED]: {
                imagePath: "images/amp_preamp.svg",
                width:     22,
                height:    32
            }
        }
    }
};

/**
 * Constante que contem o mapeamento de quais transmissões se aplicam
 * de acordo com o posicionamento horizontal e vertical delas.
 */
export const POSITION_TRANSMISSION_MAP: TransmissionPositionMap = {
    left: {
        top:    Transmission.RX,
        bottom: Transmission.TX
    },
    right: {
        top:    Transmission.TX,
        bottom: Transmission.RX,
    }
};

/**
 * Constante que define o estilo de um link configurado.
 *
 * OBS: a configuração da animação do link configurado é feita via CSS/SCSS
 * por ter a classe `flow-dash`, que é adicionada pelo CircuitDiagramDrawer.
 */
export const CIRCUIT_LINK_CONFIGURED_STYLE : CellStateStyle = {
    strokeWidth: 3,
    strokeColor: CIRCUIT_BLUE
};

/**
 * Constante que define o estilo de um link não configurado.
 */
export const CIRCUIT_LINK_NOT_CONFIGURED_STYLE : CellStateStyle = {
    strokeWidth: 1,
    strokeColor: CIRCUIT_NOT_ACTIVE,
    dashed: true,
    dashPattern: "4 8"
};

/**
 * Conjunto de constante que definem valores padrão usados no CircuitLayoutBuilder.
 */
export const MINIMUM_SIZE: number = 1;
export const DEFAULT_GAP:  number = 20;
export const TITLE_HEIGHT: number = 20;

export const AMPLIFIER_MIDDLE_SPACE:    number = 40;
export const GAP_BETWEEN_TRANSPONDERS:  number = 10;
export const SINGLE_INTERMEDIATE_SPACE: number = 18;

export const PADDING_10:            Padding = LayoutUtils.createEqualPadding(10);
export const PADDING_10_TOP_BOTTOM: Padding = LayoutUtils.createPaddingByAxis(0, 10);
export const PADDING_10_LEFT_RIGHT: Padding = LayoutUtils.createPaddingByAxis(10, 0);

/**
 * Informações dos botões do diagrama.
 */
export const DIAGRAM_BUTTONS: CircuitButtonMap = {
    [CircuitButton.DELETE]: {
        imagePath: "images/delete-link.svg",
        width:     16,
        height:    16,
        scale:     1
    }
}

/**
 * Valores padrões para as propriedade de elemento de layout do elemento raíz do diagrama.
 */
export const DEFAULT_ROOT_DIAGRAM_LAYOUT_ELEMENT: SimpleLayoutElement = {
    style: { fillColor: WHITE_COLOR, arcSize: 8, absoluteArcSize: true, shape: "rectangle", shadow: false, rounded: false },
    width: window.innerWidth,
    height: 250,
    padding: {
        left:   15,
        right:  15,
        top:    20,
        bottom: 20
    }
}

/**
 * Representação dos links como imagem para construção da legenda.
 */
export const LINKS_IMAGES_REPRESENTATION = {
    INTERNAL: {
        imagePath: "images/internal-link.svg"
    },
    EXTERNAL: {
        CONFIGURED: {
            imagePath: "images/external-link-configured.svg"
        },
        NOT_CONFIGURED: {
            imagePath: "images/external-link-not-configured.svg"
        }
    }
}

/**
 * Representação dos OSCs como imagem para construção da legenda.
 */
export const OSC_IMAGES_REPRESENTATION = {
    ACTIVE: {
        imagePath: "images/active-osc-legend.svg"
    },
    INACTIVE: {
        imagePath: "images/inactive-osc-legend.svg"
    },
    AVAILABLE: {
        imagePath: "images/available-osc-legend.svg"
    }
}
